<template>
	<v-row no-gutters>
		<v-col cols="12">
			<v-sheet dark color="#16528E">
				<v-card-text class="font-weight-light text-uppercase">
					<span class="display-1 font-weight-light text-uppercase" style="opacity: 0.8">Distributors</span>
				</v-card-text>
			</v-sheet>
		</v-col>
		<v-col cols="12">
			<div id="map" />
			<div class="overline primary--text text-right" style="position: absolute; margin-top: -30px; margin-left: 10px">
				Select country on map in order to view distributors
			</div>
		</v-col>
		<v-col cols="12">
			<v-expand-transition>
				<v-card class="opaqueBg" v-show="countryName">
					<v-card-title color="primary" class="text-h2">
						{{ countryName }}
					</v-card-title>

					<v-list color="transparent">
						<v-list-item v-for="distributor in distributors" :key="distributor.id">
							<v-list-item-avatar>
								<v-icon>mdi-map-plus</v-icon>
							</v-list-item-avatar>
							<v-list-item-content>
								<v-list-item-title v-text="distributor.name" />
								<v-list-item-subtitle>
									<a
										:href="`http://${distributor.homepage}`"
										v-text="`http://${distributor.homepage}`"
										target="_blank"
									/>
								</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>

						<v-list-item v-if="!distributors.length">
							No active distributors found
						</v-list-item>
					</v-list>
				</v-card>
			</v-expand-transition>
		</v-col>
	</v-row>
</template>

<script>
import axios from 'axios'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import CONFIG from '@/config'

function filterFeatures(features) {
	return features.reduce(
		(memo, feature) => {
			memo.push(feature.properties.countryCode)
			return memo
		},
		['in', 'countryCode']
	)
}

function getBoundingBox(point) {
	return [
		[point.x - 1, point.y - 1],
		[point.x + 1, point.y + 1]
	]
}

export default {
	name: 'DistributorMap',

	data() {
		return {
			distributors: [],
			countryName: '',
			countryCode: ''
		}
	},

	async mounted() {
		// Get data
		let result = await axios.get(CONFIG.DistributorsMapUrl)
		const mapData = result.data

		const map = new mapboxgl.Map({
			container: 'map',
			style: {
				version: 8,
				sources: {
					'simple-tiles': {
						type: 'raster',
						tiles: [
							'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'						],
						tileSize: 256
					}
				},
				layers: [
					{
						id: 'simple-tiles',
						type: 'raster',
						source: 'simple-tiles',
						minzoom: 0,
						maxzoom: 22
					}
				]
			},
			center: [0, 40],
			zoom: 1,
			minZoom: 1
		})

		const rootScope = this

		map.addControl(new mapboxgl.NavigationControl())
		map.addControl(
			new mapboxgl.AttributionControl({
				customAttribution:
					'Esri, HERE, Garmin, © OpenStreetMap contributors, and the GIS User Community | <a id ="disclaimer" class = "tooltip" data-tooltip="Alignment of boundaries is a presentation of the feature provided by our data vendors and does not imply endorsement by PRIMAR or any governing authority">DISCLAIMER</a>'
			})
		)

		map.on('load', function() {
			map.addSource('distributor-src', {
				type: 'geojson',
				data: mapData
			})

			map.addLayer({
				id: 'distributors',
				type: 'fill',
				source: 'distributor-src',
				paint: {
					'fill-opacity': 0.5,
					'fill-color': '#16528E', //'#FF9500', //  #ffffcc
					'fill-outline-color': '#000'
				},
				filter: ['>', 'distributorSize', 0]
			})

			map.addLayer({
				id: 'distributors-highlighted',
				type: 'fill',
				source: 'distributor-src',
				paint: {
					'fill-outline-color': '#484896',
					'fill-color': '#FF9500',
					'fill-opacity': 0.75
				},
				filter: ['in', 'countryCode', '']
			}) // Place polygon under these labels.

			map
				.on('mouseenter', 'distributors', () => (map.getCanvas().style.cursor = 'pointer'))
				.on('mouseleave', 'distributors', () => (map.getCanvas().style.cursor = ''))

			map.on('click', function(e) {
				// set bbox as 5px reactangle area around clicked point
				var bbox = getBoundingBox(e.point)
				var features = map.queryRenderedFeatures(bbox, {
					layers: ['distributors']
				})
				const { countryCode, countryName } = features[0].properties
				rootScope.setCountry(countryCode, countryName)

				map.setFilter('distributors-highlighted', filterFeatures(features))
			})
		})
	},
	methods: {
		async setCountry(code, name) {
			this.distributors = await this.loadDistributors(code)
			this.countryName = name
			this.countryCode = code
		},
		async loadDistributors(countryCode) {
			const URL = `${CONFIG.DistributorsUrl}/${countryCode}`
			const result = await axios.get(URL)
			return result.data
		}
	}
}
</script>

<style lang="sass" scoped>
#map
	width: 100%
	height: 500px
</style>
